<script lang="ts" setup>
import { computed, ref } from 'vue'
import { Color } from '@previo/codebooks'
import { PMaxLines } from '../typography/types'
import PIcon from '../icon/PIcon.vue'
import PSpace from '../space/PSpace.vue'
import { PSpaceSize } from '../space/types'
import PTextBadge from '../typography/system/PTextBadge.vue'
import PLoader from '../loader/PLoader.vue'
import PCard from '../card/PCard.vue'
import { PCardRound } from '../card/types'
import { PBtnTagColor } from './types'

type Props = {
  text?: null | string
  color?: null | PBtnTagColor
  iconEnd: string
  disable?: boolean
  loading?: boolean
  grow?: boolean
  cy: string
}

const props = withDefaults(defineProps<Props>(), {
  text: null,
  color: PBtnTagColor.PRIMARY,
  disable: false,
  loading: false,
  grow: false,
})

type Emits = {
  (e: 'click', evt: Event): void
}

const emits = defineEmits<Emits>()

const hover = ref<boolean>(false)

const bgColor = computed<
  | Color.PRIMARY_TRANSPARENT
  | Color.PRIMARY
  | Color.POSITIVE_TRANSPARENT
  | Color.POSITIVE
  | Color.NEGATIVE_TRANSPARENT
  | Color.NEGATIVE_STATE
  | Color.WARNING_TRANSPARENT
  | Color.WARNING
  | Color.INFO_TRANSPARENT
  | Color.INFO
  | Color.GREY_100
  | Color.GREY_200
  | Color.GREY_300
>(() => {
  if (props.disable) {
    return Color.GREY_100
  }

  if (hover.value && !props.loading) {
    switch (props.color) {
      case PBtnTagColor.PRIMARY:
        return Color.PRIMARY
      case PBtnTagColor.POSITIVE:
        return Color.POSITIVE
      case PBtnTagColor.NEGATIVE:
        return Color.NEGATIVE_STATE
      case PBtnTagColor.WARNING:
        return Color.WARNING
      case PBtnTagColor.INFO:
        return Color.INFO
      case PBtnTagColor.GREY:
        return Color.GREY_300
    }
  }

  switch (props.color) {
    case PBtnTagColor.PRIMARY:
      return Color.PRIMARY_TRANSPARENT
    case PBtnTagColor.POSITIVE:
      return Color.POSITIVE_TRANSPARENT
    case PBtnTagColor.NEGATIVE:
      return Color.NEGATIVE_TRANSPARENT
    case PBtnTagColor.WARNING:
      return Color.WARNING_TRANSPARENT
    case PBtnTagColor.INFO:
      return Color.INFO_TRANSPARENT
    case PBtnTagColor.GREY:
      return Color.GREY_100

    default:
      throw new Error('Invalid color props in PBtnTag component')
  }
})

const textColor = computed<
  | Color.PRIMARY_DARK
  | Color.POSITIVE_DARK
  | Color.NEGATIVE_STATE
  | Color.WARNING_DARK
  | Color.INFO_DARK
  | Color.WHITE
  | Color.BLACK
  | Color.GREY_500
  | Color.GREY_300
>(() => {
  if (props.disable) {
    return Color.GREY_300
  }

  if (hover.value && !props.loading) {
    switch (props.color) {
      case PBtnTagColor.WARNING:
        return Color.BLACK
      case PBtnTagColor.GREY:
        return Color.GREY_500
    }

    return Color.WHITE
  }

  switch (props.color) {
    case PBtnTagColor.PRIMARY:
      return Color.PRIMARY_DARK
    case PBtnTagColor.POSITIVE:
      return Color.POSITIVE_DARK
    case PBtnTagColor.NEGATIVE:
      return Color.NEGATIVE_STATE
    case PBtnTagColor.WARNING:
      return Color.WARNING_DARK
    case PBtnTagColor.INFO:
      return Color.INFO_DARK
    case PBtnTagColor.GREY:
      return Color.GREY_500

    default:
      throw new Error('Invalid color props in PBtnTag component')
  }
})

const click = (e: Event): void => {
  if (!props.disable && !props.loading) {
    emits('click', e)
  }
}

const isIconOnly = computed<boolean>(() => {
  return !props.text
})
</script>

<template>
  <p-card
    :color="bgColor"
    class="p-display-flex p-items-center p-no-wrap ds-btn-tag"
    :class="{
      'p-fit-width': !grow,
      'p-cursor-pointer': !loading && !disable,
      'p-cursor-not-allowed': disable,
      'ds-btn-tag__icon-only': isIconOnly,
    }"
    :data-cy="cy"
    @mouseenter="hover = true"
    @mouseleave="hover = false"
    @click="click"
  >
    <div class="ds-btn-tag__content" :class="{ 'ds-btn-tag__content__hidden': loading }">
      <template v-if="text">
        <p-text-badge :text="text" :color="textColor" :max-lines="PMaxLines.ONE" />
        <p-space :mobile="PSpaceSize.SM" />
      </template>

      <p-space :mobile="PSpaceSize.GROW" />

      <p-card class="ds-btn-tag__icon" :color="Color.WHITE_TRANSPARENT" :round="PCardRound.XS">
        <p-icon :icon="iconEnd" :color="textColor" :size="16" />
      </p-card>

      <slot />
    </div>

    <div v-if="loading" class="ds-btn-tag__loader-container">
      <p-loader :size="16" :color="textColor" />
    </div>
  </p-card>
</template>

<style lang="scss" scoped>
@import '../../assets/scss/shared/ds.variables.module';
@import '../../assets/scss/shared/variables/ds.radius';

.ds-btn-tag {
  padding: 2px 2px 2px 12px;
  border-radius: $radius-p-btn-tag !important;
  position: relative;

  &__icon-only {
    padding: 2px;
  }

  &__icon {
    padding: 2px;
  }

  &__content {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    width: 100%;

    &__hidden {
      opacity: 0;
      pointer-events: none;
    }
  }

  &__loader-container {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
